



import { useState } from 'react';
import { Lock, Eye, EyeOff, ArrowLeft, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const PasswordLogin = ({ email, onBack, onSuccess }) => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [buttonState, setButtonState] = useState('default');
  const [remember, setRemember] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      setError('Please enter your password');
      return;
    }
    setButtonState('loading');
    try {
      const response = await axios.post('https://api.nfcdesignstudio.com/API/v1/login', {
        email: email,
        password: password,
      });
      if (response.status === 200) {
        sessionStorage.setItem('token', response.data.token);
        sessionStorage.setItem('email', email);
        setButtonState('success');
        setTimeout(() => {
          if (onSuccess) onSuccess();
          navigate('/builder');
        }, 800);
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error || 'Login failed. Try again.';
      setError(errorMessage);
      setButtonState('error');
      setTimeout(() => setButtonState('default'), 2000);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    paddingLeft: '48px',
    paddingRight: '48px',
    borderRadius: '12px',
    border:
      error || buttonState === 'error'
        ? '2px solid #f87171'
        : '2px solid #d1d5db',
    backgroundColor: '#f3f4f6',
    outline: 'none',
    color: '#111827',
    boxSizing: 'border-box',
  };

  const buttonStyle = {
    width: '100%',
    padding: '12px',
    borderRadius: '12px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor:
      buttonState === 'loading' || buttonState === 'success'
        ? 'not-allowed'
        : 'pointer',
    background:
      buttonState === 'success'
        ? '#4ade80'
        : buttonState === 'error'
          ? '#f87171'
          : 'linear-gradient(to right, #6366f1, #ec4899)',
    color: 'white',
    border: 'none',
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        maxWidth: '400px',
        margin: '0 auto',
      }}
    >
      {/* Back Button */}
      <button
        type="button"
        onClick={onBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'none',
          border: 'none',
          color: '#6b7280',
          cursor: 'pointer',
        }}
      >
        <ArrowLeft size={16} /> Change email
      </button>

      {/* Email Display */}
      <div
        style={{
          padding: '12px',
          backgroundColor: '#f3f4f6',
          borderRadius: '12px',
          border: '1px solid #d1d5db',
        }}
      >
        <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          Signing in as
        </p>
        <p style={{ fontWeight: '500', color: '#111827' }}>{email}</p>
      </div>

      {/* Password Input */}
      <div style={{ position: 'relative' }}>
        <label
          style={{
            display: 'block',
            fontWeight: '500',
            marginBottom: '4px',
            color: '#111827',
          }}
        >
          Password
        </label>

        <div style={{ position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              left: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
            }}
          >
            <Lock size={20} />
          </div>

          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError('');
            }}
            placeholder="Enter your password"
            style={inputStyle}
          />

          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={{
              position: 'absolute',
              right: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9ca3af',
            }}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>

        {error && (
          <p style={{ color: '#f87171', fontSize: '0.875rem', marginTop: '4px' }}>
            {error}
          </p>
        )}
      </div>

      {/* Login Button */}
      <button
        type="submit"
        style={buttonStyle}
        disabled={buttonState === 'loading' || buttonState === 'success'}
      >
        {buttonState === 'loading' && (
          <>
            <Loader2 size={20} /> Signing in...
          </>
        )}
        {buttonState === 'success' && (
          <>
            <Check size={20} /> Success!
          </>
        )}
        {(buttonState === 'default' || buttonState === 'error') &&
          'Sign In'}
      </button>

      {/* Remember Me */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          type="checkbox"
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
        />
        <label style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          Remember me for 30 days
        </label>
      </div>
    </form>
  );
};

export default PasswordLogin;
